/**
 * Validation functions for form fields
 * These functions provide standardized validation for common form inputs
 */

import { isValidEmailAddress } from '@/utils/validation/emailValidation';

interface ValidationResult {
  valid: boolean;
  message: string;
}

/**
 * Validates an email address
 * @param email The email address to validate
 * @param required Whether the field is required
 * @returns Validation result with valid status and error message
 */
export const validateEmail = (email: string, required: boolean = false): ValidationResult => {
  if (!email) {
    return {
      valid: !required,
      message: required ? "Email address is required" : ""
    };
  }
  
  const isValid = isValidEmailAddress(email);
  
  return {
    valid: isValid,
    message: isValid ? "" : "Please enter a valid email address"
  };
};

/**
 * Validates a phone number in (123) 456-7890 format
 * @param phone The phone number to validate
 * @param required Whether the field is required
 * @returns Validation result with valid status and error message
 */
export const validatePhone = (phone: string, required: boolean = false): ValidationResult => {
  if (!phone) {
    return {
      valid: !required,
      message: required ? "Phone number is required" : ""
    };
  }

  // Check for complete phone number format: (123) 456-7890
  const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
  const isValid = phoneRegex.test(phone);
  
  return {
    valid: isValid,
    message: isValid ? "" : "Please enter a valid phone number in the format (123) 456-7890"
  };
};

/**
 * Validates a ZIP code in 12345 or 12345-6789 format
 * @param zip The ZIP code to validate
 * @param required Whether the field is required
 * @returns Validation result with valid status and error message
 */
export const validateZipCode = (zip: string, required: boolean = false): ValidationResult => {
  if (!zip) {
    return {
      valid: !required,
      message: required ? "ZIP code is required" : ""
    };
  }
  
  // ZIP code must be 5 digits or ZIP+4 format (12345 or 12345-6789)
  const zipRegex = /^\d{5}(-\d{4})?$/;
  const isValid = zipRegex.test(zip);
  
  return {
    valid: isValid,
    message: isValid ? "" : "Please enter a valid ZIP code (12345 or 12345-6789)"
  };
};

/**
 * Validates a URL
 * @param url The URL to validate
 * @param required Whether the field is required
 * @returns Validation result with valid status and error message
 */
export const validateUrl = (url: string, required: boolean = false): ValidationResult => {
  if (!url) {
    return {
      valid: !required,
      message: required ? "URL is required" : ""
    };
  }
  
  try {
    // This will throw for invalid URLs
    new URL(url);
    return {
      valid: true,
      message: ""
    };
  } catch (e) {
    return {
      valid: false,
      message: "Please enter a valid URL (e.g., https://www.example.com)"
    };
  }
};

/**
 * Validates a required field has a value
 * @param value The field value
 * @param fieldName The name of the field for the error message
 * @returns Validation result with valid status and error message
 */
export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  const isValid = !!value && value.trim().length > 0;
  
  return {
    valid: isValid,
    message: isValid ? "" : `${fieldName} is required`
  };
}; 